import React, { useState } from 'react';
import { User, Mail, Heart, ArrowLeft } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { mobilePageBackground } from '../../../services/mobileStyleService';
import { mlbTeams, getTeamByName, getBaseballBackground } from './baseballAssets';

const MobileSignUpStep1 = ({ onNext, onBack, initialData, error }) => {
  const [formData, setFormData] = useState({
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    email: initialData?.email || '',
    favoriteTeam: initialData?.favoriteTeam || ''
  });

  const selectedTeam = getTeamByName(formData.favoriteTeam);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext(formData);
  };

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ background: mobilePageBackground, ...getBaseballBackground() }}
    >
      {/* Header */}
      <div className={`flex items-center justify-between px-6 py-4 border-b ${dynastyTheme.classes.border.neutral}`}>
        <button
          onClick={onBack}
          className={`flex items-center gap-2 ${dynastyTheme.classes.text.neutralLighter} hover:${dynastyTheme.classes.text.white} ${dynastyTheme.classes.transition}`}
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
        <h2 className={`text-lg font-semibold ${dynastyTheme.classes.text.white}`}>
          Join the League
        </h2>
        <div className={`text-sm ${dynastyTheme.classes.text.neutralLighter}`}>
          1/2
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 px-6 py-8 overflow-y-auto">
        <form onSubmit={handleSubmit} className="space-y-6 max-w-md mx-auto">
          {/* Welcome Message */}
          <div className="text-center mb-8">
            <div className="text-4xl mb-3">🏟️</div>
            <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white} mb-2`}>
              Welcome to the Show!
            </h3>
            <p className={`text-sm ${dynastyTheme.classes.text.neutralLighter}`}>
              Let's set up your Dynasty Dugout account
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className={`${dynastyTheme.components.badge.error} p-4 rounded-lg text-center`}>
              {error}
            </div>
          )}

          {/* First Name */}
          <div>
            <label className={dynastyTheme.components.label}>
              First Name
            </label>
            <div className="relative">
              <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${dynastyTheme.classes.text.neutralLighter}`} />
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={`${dynastyTheme.components.input} w-full pl-12 pr-4 py-3 text-base`}
                placeholder="Tony"
                required
                autoComplete="given-name"
              />
            </div>
          </div>

          {/* Last Name */}
          <div>
            <label className={dynastyTheme.components.label}>
              Last Name
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={`${dynastyTheme.components.input} w-full px-4 py-3 text-base`}
              placeholder="Straka"
              required
              autoComplete="family-name"
            />
          </div>

          {/* Email */}
          <div>
            <label className={dynastyTheme.components.label}>
              Email
            </label>
            <div className="relative">
              <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${dynastyTheme.classes.text.neutralLighter}`} />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`${dynastyTheme.components.input} w-full pl-12 pr-4 py-3 text-base`}
                placeholder="your@email.com"
                required
                autoComplete="email"
              />
            </div>
          </div>

          {/* Favorite Team */}
          <div>
            <label className={dynastyTheme.components.label}>
              Favorite Team
            </label>
            <div className="relative">
              <Heart className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${dynastyTheme.classes.text.neutralLighter}`} />
              <select
                name="favoriteTeam"
                value={formData.favoriteTeam}
                onChange={handleChange}
                className={`${dynastyTheme.components.input} w-full pl-12 pr-4 py-3 text-base appearance-none cursor-pointer`}
                required
              >
                <option value="">Select your favorite team</option>
                {mlbTeams.map((team) => (
                  <option key={team.abbrev} value={team.name}>
                    {team.name}
                  </option>
                ))}
              </select>
              <div className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${dynastyTheme.classes.text.neutralLighter}`}>
                ▼
              </div>
            </div>
          </div>

          {/* Team Logo/Badge Display */}
          {selectedTeam && (
            <div 
              className={`rounded-lg p-4 border-2 ${dynastyTheme.classes.transition} animate-[slideUp_0.3s_ease-out]`}
              style={{
                borderColor: selectedTeam.primary,
                backgroundColor: `${selectedTeam.primary}15`
              }}
            >
              <div className="flex items-center gap-4">
                {/* Team Abbreviation Circle */}
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl border-2"
                  style={{
                    backgroundColor: selectedTeam.primary,
                    borderColor: selectedTeam.secondary
                  }}
                >
                  {selectedTeam.abbrev}
                </div>
                
                {/* Team Info */}
                <div className="flex-1">
                  <p className={`text-lg font-semibold ${dynastyTheme.classes.text.white}`}>
                    {selectedTeam.name}
                  </p>
                  <p className={`text-sm ${dynastyTheme.classes.text.neutralLighter}`}>
                    Go {selectedTeam.name.split(' ').pop()}! ⚾
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Next Button */}
          <button
            type="submit"
            className={`${dynastyTheme.utils.getComponent('button', 'primary', 'lg')} w-full flex items-center justify-center gap-2`}
          >
            Next: Set Password
            <span>→</span>
          </button>
        </form>
      </div>

      {/* Inline CSS for animation */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default MobileSignUpStep1;
